import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getPopularServices } from '../../service/store/serviceSlice';
import { getServiceIconUrl } from './serviceIconMap';
import { bookingNavigationState } from '../../booking/utils/bookingNavigationState';

/**
 * QuickServiceGrid Component
 * One-tap service selection for minimal-click booking
 */
const QuickServiceGrid = ({ services = [], selectedAddress = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { popularServices, loading } = useSelector((state) => state.service);
  const { isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    // Load popular services if not already loaded
    if (popularServices.length === 0) {
      dispatch(getPopularServices(8)).catch(error => {
      });
    }
  }, [dispatch, popularServices.length]);

  const defaultServices = [
    {
      id: 'plumbing',
      name: 'Plumbing',
      icon: '🔧',
      description: 'Leaky tap? Clogged sink?',
      color: 'from-blue-500 to-blue-700',
      badge: 'Most Booked',
    },
    {
      id: 'electrical',
      name: 'Electrical',
      icon: '⚡',
      description: 'Wiring, outlets, switches',
      color: 'from-yellow-500 to-yellow-700',
      image: '/fixome_electrical.png',
      badge: 'Most Booked',
    },
    {
      id: 'ac-repair',
      name: 'AC Repair',
      icon: '❄️',
      description: 'Cooling & maintenance',
      color: 'from-cyan-500 to-cyan-700',
      badge: 'Popular',
    },
    {
      id: 'deep-cleaning',
      name: 'Deep Cleaning',
      icon: '🧽',
      description: 'Make your home shine',
      color: 'from-green-500 to-green-700',
      badge: 'Popular',
    },
    {
      id: 'moving',
      name: 'Moving',
      icon: '📦',
      description: 'Packing & relocation',
      color: 'from-purple-500 to-purple-700',
    },
    {
      id: 'painting',
      name: 'Painting',
      icon: '🎨',
      description: 'Interior & exterior',
      color: 'from-pink-500 to-pink-700',
    },
    {
      id: 'pest-control',
      name: 'Pest Control',
      icon: '🐛',
      description: 'Safe & effective removal',
      color: 'from-orange-500 to-orange-700',
    },
    {
      id: 'appliance-repair-and-service',
      name: 'Appliance Repair',
      icon: '⚙️',
      description: 'Fix your appliances fast',
      color: 'from-indigo-500 to-indigo-700',
    },
  ];

  // Badges by slug — merged into all service sources (API or default).
  const serviceBadges = {
    'plumbing': 'Most Booked',
    'electrical': 'Most Booked',
    'ac-repair': 'Popular',
    'deep-cleaning': 'Popular',
  };

  // Starting price per slug (confirmed by Anish, Oct 1 2026). Ratings removed: not backed by real review counts.
  const serviceStats = {
    'electrical': { price: 299 },
    'plumbing': { price: 299 },
    'deep-cleaning': { price: 499 },
    'ac-repair': { price: 499 },
    'appliance-repair': { price: 399 },
    'moving': { price: 599 },
    'painting': { price: 499 },
    'pest-control': { price: 499 },
  };

  // Technician photos keyed by service slug (restored photo tiles)
  const localServiceImages = {
    'electrical': '/fixome_electrical.webp',
    'plumbing': '/fixome_plumbing.webp',
    'ac-repair': '/fixome_ac_repair.webp',
    'deep-cleaning': '/fixome_deep_cleaning.webp',
    'moving': '/fixome_moving.webp',
    'painting': '/fixome_painting.webp',
    'pest-control': '/fixome_pest_control.webp',
    'appliance-repair': '/fixome_appliance_repair.webp',
  };

  // Use dynamic services if available, otherwise fall back to default services
  const rawServiceList = services.length > 0 
    ? services 
    : popularServices.length > 0 
      ? popularServices 
      : defaultServices;

  // Merge local images, starting prices, and badges into whichever list is used
  const serviceList = rawServiceList.map((service) => {
    const slug = (service.slug || service.id || service._id || service.name || '').toLowerCase();
    const matchedBadgeKey = Object.keys(serviceBadges).find(key => slug.includes(key));
    const matchedImageKey = Object.keys(localServiceImages).find(key => slug.includes(key));
    const matchedStatsKey = Object.keys(serviceStats).find(key => slug.includes(key));
    return {
      ...service,
      stats: matchedStatsKey ? serviceStats[matchedStatsKey] : null,
      photo: matchedImageKey ? localServiceImages[matchedImageKey] : null,
      badge: service.badge || (matchedBadgeKey ? serviceBadges[matchedBadgeKey] : null),
    };
  });

  const handleServiceClick = (service) => {
    const serviceId = service._id || service.id;
    const bookingPath = `/booking/new/${serviceId}`;
    const bookingState = bookingNavigationState({
      selectedAddress,
      serviceLabel: service.name,
    });

    if (!isAuthenticated) {
      navigate('/login', {
        state: {
          from: { pathname: bookingPath, state: bookingState },
          ...(selectedAddress ? { selectedAddress } : {}),
        },
      });
    } else {
      navigate(bookingPath, { state: bookingState });
    }
  };

  return (
    <div className="py-2 md:py-4">

      {loading && serviceList.length === 0 ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-500">Loading services...</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4">
          {serviceList.map((service) => (
          <button
            key={service._id || service.id}
            type="button"
            onClick={() => handleServiceClick(service)}
            aria-label={`Book ${service.name}`}
            className="group relative overflow-hidden rounded-2xl md:rounded-3xl shadow-soft hover:shadow-float transition-all duration-300 hover:-translate-y-1 active:scale-[0.98] bg-white flex flex-col h-full border border-slate-100 text-left"
            style={{ padding: 0 }}
          >
            {service.photo ? (
              <div className="relative w-full overflow-hidden rounded-t-2xl md:rounded-t-3xl" style={{ aspectRatio: '2 / 1' }}>
                <img
                  src={service.photo}
                  alt={service.name}
                  width={326}
                  height={163}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover block group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            ) : (
              <div className="relative w-full bg-slate-50 flex items-center justify-center rounded-t-2xl md:rounded-t-3xl" style={{ aspectRatio: '2 / 1' }}>
                <img src={getServiceIconUrl(service)} alt="" width="48" height="48" loading="lazy" decoding="async" />
              </div>
            )}
            <div className="p-3 md:p-4 text-left flex-1 flex flex-col">
              {service.badge && (
                <span className={`self-start inline-flex items-center gap-1 rounded-full text-xs font-semibold px-2.5 py-1 mb-2 leading-none ${
                  service.badge === 'Most Booked' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'
                }`}>
                  {service.badge === 'Most Booked' ? (
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 1.5s1 3.5-1.5 6.5c-1.6 1.9-4 3.4-4 7a6 6 0 0 0 12 0c0-3.2-1.8-5.6-3-7 0 2-1 3.5-2.5 3.5 1-3 .5-7.5-1-10zM12 22a4 4 0 0 1-4-4c0-2 1.5-3 2.5-4.5.3 1.5 1.5 2.5 2.5 2.5 0-1 .5-2 1-2.5.8 1 2 2.6 2 4.5a4 4 0 0 1-4 4z"/></svg>
                  ) : (
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="12" width="4" height="8" rx="1"/><rect x="10" y="7" width="4" height="13" rx="1"/><rect x="16" y="3" width="4" height="17" rx="1"/></svg>
                  )}
                  {service.badge}
                </span>
              )}
              <h3 className="font-bold text-base md:text-sm text-slate-900 mb-1 line-clamp-2 tracking-tight">
                {service.name}
              </h3>
              {(service.shortDescription || service.description) && (
                <p className="text-base md:text-sm text-slate-500 line-clamp-2">
                  {service.shortDescription || service.description}
                </p>
              )}
              <div className="mt-auto pt-3 flex flex-col items-start gap-2 lg:flex-row lg:items-center lg:justify-between lg:gap-1.5">
                {service.stats && (
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] whitespace-nowrap">
                    <span className="text-slate-900 font-bold">From ₹{service.stats.price}</span>
                  </div>
                )}
                <span className="inline-flex items-center rounded-full bg-emerald-600 group-hover:bg-emerald-700 text-white text-[12px] font-bold px-3 py-1.5 lg:px-2.5 leading-none whitespace-nowrap shrink-0 transition-colors">
                  Book now
                </span>
              </div>
            </div>
          </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuickServiceGrid;

